import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import { catalog, links, scopeKey } from '../api/endpoints.ts';
import type { RelatedProducts, StorefrontItem } from '../api/types.ts';
import { formatPara, para, perUnit } from '../core/money.ts';
import { formatWeight, isNutritionEmpty, scaledNutrition } from '../core/nutrition.ts';
import { useStore } from '../core/store.ts';
import { longDay, useLang, useStrings, type Strings } from '../i18n/index.ts';
import { addressBookStore, storefrontScope } from '../state/address-book.ts';
import { addAllToCart, canAdd, cartStore, decreaseInCart, qtyOf } from '../state/cart.ts';
import { launchCartFlight } from '../state/cart-flight.ts';
import { addWithChecks, availabilityOf, verifyAndFix } from '../state/cart-sync.ts';
import { ageVerdict, askAddress, askAge } from '../state/gates.ts';
import { stockStore } from '../state/stores.ts';
import { notify } from '../state/toasts.ts';
import { Icon, type IconName } from '../ui/icons.tsx';
import { cx, GlassButton, PriceLabel, RemoteImage, SaleBadge, ScreenTitle } from '../ui/kit.tsx';
import { NutritionCells } from '../ui/NutritionSummary.tsx';
import { FavoriteButton, ProductLane, ProductNavContext } from '../ui/ProductTile.tsx';
import { Screen } from '../ui/Screen.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { ReportSheet } from './ReportSheet.tsx';
import '../styles/product.css';

/**
 * Карточка товара: фото на белом, название, цена и старая цена, цена за килограмм, «избранное»,
 * «поделиться» и «i», бейджи, описание и срок акции, блоки «Часто берут вместе» и «Вам может
 * понравиться», мелкая ссылка «Пожаловаться». Вкладки на карточке спрятаны, внизу одна
 * капсула: степпер и «В корзину».
 */
export function ProductScreen({ item: initial, embedded }: { item: StorefrontItem; embedded?: boolean }) {
  const strings = useStrings();
  const nav = useContext(ProductNavContext);
  const lang = useLang();
  const cart = useStore(cartStore);
  const scope = storefrontScope(useStore(addressBookStore));
  const stock = useStore(stockStore);

  // Снимок товара с витрины; остаток и цена сверяются со складом при открытии, дальше остаток
  // правит лента событий.
  const [item, setItem] = useState(initial);
  const [related, setRelated] = useState<RelatedProducts | null>(null);
  const [showsInfo, setShowsInfo] = useState(false);
  const [showsReport, setShowsReport] = useState(false);
  const [wholeNutrition, setWholeNutrition] = useState(false);
  /** Количество, которое человек набирает до первого «В корзину». */
  const [pendingQty, setPendingQty] = useState(1);
  const photo = useRef<HTMLDivElement>(null);

  const quantity = qtyOf(cart, item.id);
  const available = item.availableQty > 0;

  useEffect(() => {
    let cancelled = false;
    catalog
      .related(item.id, scope, lang)
      .then((loaded) => !cancelled && setRelated(loaded))
      .catch(() => undefined);
    // Сверка со складом при открытии: снимок витрины мог устареть. Снятый с продажи товар
    // показывается как закончившийся.
    void availabilityOf(item.id).then((fresh) => {
      if (fresh && !cancelled) {
        setItem((current) => ({ ...current, availableQty: fresh.isActive ? fresh.availableQty : 0, priceRsd: fresh.priceRsd }));
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, scopeKey(scope), lang]);

  useEffect(() => {
    const event = stock.last;
    if (event && event.productId === item.id) setItem((current) => ({ ...current, availableQty: event.availableQty }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock.version]);

  const addPending = () => {
    if (!available || quantity > 0) return;
    // Первый товар спрашивает адрес, товар 18+ — возраст: набранное количество ляжет после ответа
    // одной штукой, как с плитки.
    if (scope.kind === 'none') return askAddress(item);
    if (item.ageRestricted) {
      const verdict = ageVerdict();
      if (verdict === 'underage') return notify(strings.ageGateUnderage);
      if (verdict === 'unknown') return askAge(item);
    }
    launchCartFlight(item.photoUrl, photo.current);
    addAllToCart([{ item, qty: pendingQty }]);
    void verifyAndFix([item.id]);
    setPendingQty(1);
  };

  const share = () => {
    const url = links.productShare(item.id);
    const text = `${item.name} · ${formatPara(para(item.priceRsd))}`;
    if (navigator.share) void navigator.share({ title: item.name, text, url }).catch(() => undefined);
    else void navigator.clipboard?.writeText(url).then(() => notify(url));
  };

  const header = <Header item={item} strings={strings} onShare={share} onInfo={() => setShowsInfo(true)} />;
  const description = <DescriptionBlock item={item} strings={strings} onInfo={() => setShowsInfo(true)} />;
  const nutrition = (
    <NutritionTable item={item} strings={strings} quantity={quantity} whole={wholeNutrition} onWhole={setWholeNutrition} />
  );

  return (
    <Screen noDock overlayNav embedded={embedded ?? false} className="product-screen">
      <div className="page product-page">
        <div className="product-top">
          <div className="product-media" ref={photo}>
            <Gallery item={item} />
          </div>
          <div className="product-summary stack" style={{ gap: 18 }}>
            {header}
            {description}
            {nutrition}
          </div>
        </div>

        {related && related.oftenBoughtWith.length > 0 && <LaneBlock title={strings.oftenBoughtWith} items={related.oftenBoughtWith} />}
        {related && related.similar.length > 0 && <LaneBlock title={strings.youMightLike} items={related.similar} />}

        {/* «Пожаловаться» — служебная ссылка, а не действие карточки: мелкая и приглушённая. */}
        <button type="button" className="product-report" onClick={() => setShowsReport(true)}>
          <FlagIcon />
          {strings.report}
        </button>
      </div>

      {/* Закреплённая панель: одна капсула в цвет бренда. Пока товара нет в корзине, степпер
          набирает количество для первого добавления; когда он в корзине, кнопка ведёт в неё. */}
      <div className="bottom-bar product-bar">
        <div className={cx('product-capsule', !available && 'is-off')}>
          {quantity > 0 ? (
            <BarStepper
              value={quantity}
              removes={quantity === 1}
              canIncrease={canAdd(cart, item.id) && quantity < item.availableQty}
              onDecrease={() => decreaseInCart(item.id)}
              onIncrease={() => addWithChecks(item)}
              label={strings.a11yQuantity}
            />
          ) : (
            <BarStepper
              value={pendingQty}
              removes={false}
              canIncrease={pendingQty < Math.max(item.availableQty, 1)}
              onDecrease={() => setPendingQty((value) => Math.max(1, value - 1))}
              onIncrease={() => setPendingQty((value) => value + 1)}
              label={strings.a11yQuantity}
            />
          )}
          <button type="button" className="product-capsule-action" disabled={!available} onClick={() => (quantity > 0 ? nav.goToCart() : addPending())}>
            <span>{quantity > 0 ? strings.goToCart : strings.addToCart}</span>
            <b className="t-numeric">{formatPara(para(item.priceRsd) * BigInt(Math.max(quantity, pendingQty)))}</b>
          </button>
        </div>
      </div>

      {showsInfo && <ProductInfoSheet item={item} onClose={() => setShowsInfo(false)} />}
      {showsReport && <ReportSheet productId={item.id} onClose={() => setShowsReport(false)} />}
    </Screen>
  );
}

function FlagIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 21V4M5 4.500c4-2 6 2 10 .5 1.300-.5 2.500-.5 4 0v9c-1.500-.5-2.700-.5-4 0-4 1.500-6-2.500-10-.5" />
    </svg>
  );
}

/**
 * Галерея на белом фоне экрана: листается постранично, точки внизу показывают позицию.
 * Зацикленное видео идёт первым, дальше фото; единственное фото показывается без листания.
 */
function Gallery({ item }: { item: StorefrontItem }) {
  const strip = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  // Системная настройка «уменьшить движение» выключает зацикленное видео.
  const showsVideo = item.videoUrl !== null && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const photos = item.photos && item.photos.length > 0 ? item.photos : item.photoUrl ? [item.photoUrl] : [];
  const pages: ReactNode[] = [];
  if (showsVideo && item.videoUrl) pages.push(<ProductVideo key="video" url={item.videoUrl} poster={item.photoUrl} />);
  for (const url of photos) {
    pages.push(
      <div key={url} className="gallery-photo">
        <RemoteImage src={url} eager />
      </div>,
    );
  }
  if (pages.length === 0) pages.push(<div key="empty" className="gallery-photo"><RemoteImage src={null} /></div>);

  if (pages.length === 1) return <div className="gallery-page">{pages[0]}</div>;

  return (
    <>
      <div
        className="gallery-strip"
        ref={strip}
        aria-roledescription="carousel"
        onScroll={(event) => {
          const node = event.currentTarget;
          if (node.clientWidth > 0) setPage(Math.round(node.scrollLeft / node.clientWidth));
        }}
      >
        {pages.map((content, index) => (
          <div key={index} className="gallery-page">
            {content}
          </div>
        ))}
      </div>
      <div className="gallery-dots" aria-hidden="true">
        {pages.map((_, index) => (
          <button
            key={index}
            type="button"
            tabIndex={-1}
            className={cx(index === page && 'is-current')}
            onClick={() => strip.current?.scrollTo({ left: index * strip.current.clientWidth, behavior: 'smooth' })}
          />
        ))}
      </div>
    </>
  );
}

/** Страница видео: под ним главное фото, видео проявляется, когда готов первый кадр. */
function ProductVideo({ url, poster }: { url: string; poster: string | null }) {
  const [ready, setReady] = useState(false);
  return (
    <div className="gallery-video">
      <div className="gallery-photo">
        <RemoteImage src={poster} eager />
      </div>
      <video className={cx(ready && 'is-ready')} src={url} muted loop autoPlay playsInline preload="auto" onCanPlay={() => setReady(true)} />
    </div>
  );
}

function Header(props: { item: StorefrontItem; strings: Strings; onShare: () => void; onInfo: () => void }) {
  const { item, strings } = props;
  const badges: { text: string; icon: IconName }[] = [];
  if (item.ageRestricted) badges.push({ text: strings.badgeAge, icon: 'shield' });
  if (item.isNew) badges.push({ text: strings.badgeNew, icon: 'sparkles' });
  if (item.tags.vegan) badges.push({ text: strings.tagVegan.toUpperCase(), icon: 'leaf' });
  if (item.tags.glutenFree) badges.push({ text: strings.tagGlutenFree.toUpperCase(), icon: 'wheat' });
  if (item.tags.sugarFree) badges.push({ text: strings.tagSugarFree.toUpperCase(), icon: 'cube' });

  // Фасовка и цена за единицу одной приглушённой строкой: «120 g · 1.992,00 RSD/kg». Фасовка,
  // уже названная в имени («Banane 1kg» и «1 kg»), не повторяется; у фасовки в килограмм цена
  // за единицу повторяет цену и опускается.
  const squeeze = (text: string) => text.toLowerCase().replace(/\s/g, '');
  const size = item.sizeLabel && !squeeze(item.name).includes(squeeze(item.sizeLabel)) ? item.sizeLabel : null;
  const unit = perUnit(para(item.priceRsd), item.sizeLabel);
  const unitPrice = unit && unit.price !== para(item.priceRsd) ? `${formatPara(unit.price)}/${unit.unit}` : null;
  const meta = [size, unitPrice].filter((part): part is string => part !== null);

  return (
    <div className="stack gutter" style={{ gap: 8 }}>
      <div className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
        <h1 className="t-display grow product-name">{item.name}</h1>
        <FavoriteButton productId={item.id} size={40} />
        <button type="button" className="product-round" aria-label={strings.share} onClick={props.onShare}>
          <Icon name="share" size={18} weight={2.2} />
        </button>
        <button type="button" className="product-round" aria-label={strings.productInfo} onClick={props.onInfo}>
          <Icon name="info" size={18} weight={2.6} />
        </button>
      </div>

      <div className="product-price-row">
        <span className="row" style={{ gap: 8 }}>
          <PriceLabel price={item.priceRsd} compareAt={item.compareAtPriceRsd} size={24} />
          {/* Скидка стоит у цены, а не на фото: там она закрывала бы видео. */}
          {item.compareAtPriceRsd && <SaleBadge price={item.priceRsd} compareAt={item.compareAtPriceRsd} />}
        </span>
        {badges.length > 0 && (
          <span className="product-badges">
            {badges.map((badge) => (
              <span key={badge.text} className="product-badge">
                <Icon name={badge.icon} size={12} weight={2.6} />
                {badge.text}
              </span>
            ))}
          </span>
        )}
      </div>

      {meta.length > 0 && <p className="t-numeric t-muted" style={{ fontSize: 14, fontWeight: 500 }}>{meta.join(' · ')}</p>}

      {item.availableQty <= 5 && (
        <p className="product-stock">{item.availableQty <= 0 ? strings.outOfStock : strings.onlyLeft(item.availableQty)}</p>
      )}
    </div>
  );
}

/**
 * Описание в две строки и ссылка на лист с составом, добавками, аллергенами и пищевой
 * ценностью: кнопку «i» в шапке замечают не все, а эти данные обязательны по закону.
 * Без описания ссылка называется «Информация о товаре».
 */
function DescriptionBlock({ item, strings, onInfo }: { item: StorefrontItem; strings: Strings; onInfo: () => void }) {
  const lang = useLang();
  return (
    <div className="stack gutter" style={{ gap: 10 }}>
      {item.promoUntil && <p className="product-promo">{strings.promoUntil(longDay(lang, item.promoUntil))}</p>}
      {item.description && <p className="product-description">{item.description}</p>}
      <button type="button" className="product-more" onClick={onInfo}>
        {item.description ? strings.readMore : strings.productInfo}
        <Icon name="chevronRight" size={13} weight={2.8} />
      </button>
    </div>
  );
}

/**
 * Пищевая ценность: переключатель между 100 г и всей упаковкой (или количеством в корзине у
 * весового товара), под ним четыре значения с подписями.
 */
function NutritionTable(props: { item: StorefrontItem; strings: Strings; quantity: number; whole: boolean; onWhole: (next: boolean) => void }) {
  const { item, strings } = props;
  const lang = useLang();
  if (isNutritionEmpty(item.nutrition)) return null;

  let whole: { label: string; grams: number } | null = null;
  if (item.netWeightG && item.netWeightG > 0) {
    const weighed = item.unit === 'kg' || item.unit === 'l';
    const grams = weighed ? item.netWeightG * Math.max(props.quantity, 1) : item.netWeightG;
    whole = { label: weighed ? strings.nutritionAmount(formatWeight(grams, lang)) : strings.nutritionWhole, grams };
  }
  const showsWhole = props.whole && whole !== null;

  return (
    <div className="stack gutter" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 8 }}>
        <button type="button" className={cx('nutrition-pill', !showsWhole && 'is-selected')} onClick={() => props.onWhole(false)}>
          {item.unit === 'l' ? strings.per100ml : strings.per100g}
        </button>
        {whole && (
          <button type="button" className={cx('nutrition-pill', showsWhole && 'is-selected')} onClick={() => props.onWhole(true)}>
            {whole.label}
          </button>
        )}
      </div>
      <NutritionCells amount={scaledNutrition(item.nutrition, showsWhole && whole ? whole.grams : 100)} />
    </div>
  );
}

function LaneBlock({ title, items }: { title: string; items: StorefrontItem[] }) {
  return (
    <section className="stack" style={{ gap: 12 }}>
      <hr className="hairline" style={{ marginInline: 'var(--gutter)' }} />
      <h2 className="t-display gutter" style={{ fontSize: 22 }}>
        {title}
      </h2>
      <ProductLane items={items} />
    </section>
  );
}

function BarStepper(props: { value: number; removes: boolean; canIncrease: boolean; onDecrease: () => void; onIncrease: () => void; label: string }) {
  const strings = useStrings();
  return (
    <div className="product-stepper" role="group" aria-label={props.label}>
      <button type="button" aria-label={props.removes ? strings.a11yRemoveFromCart : strings.a11yDecrease} onClick={props.onDecrease}>
        <Icon name={props.removes ? 'trash' : 'minus'} size={16} weight={2.8} />
      </button>
      <b className="t-numeric">{props.value}</b>
      <button type="button" aria-label={strings.a11yIncrease} disabled={!props.canIncrease} onClick={props.onIncrease}>
        <Icon name="plus" size={16} weight={2.8} />
      </button>
    </div>
  );
}

/**
 * «Информация о товаре»: штрихкод, страна, состав, добавки, аллергены, срок и условия
 * хранения, пищевая ценность. В Сербии эти данные обязательны по закону.
 */
function ProductInfoSheet({ item, onClose }: { item: StorefrontItem; onClose: () => void }) {
  const strings = useStrings();
  const lang = useLang();

  // Показываются только заполненные поля: пустая строка с прочерком места не занимает.
  const row = (title: string, value: string | null | undefined) =>
    value ? (
      <div className="stack" style={{ gap: 4 }}>
        <h3 className="t-display" style={{ fontSize: 18 }}>
          {title}
        </h3>
        <p className="t-muted" style={{ fontSize: 17 }}>
          {value}
        </p>
      </div>
    ) : null;

  const grams = (value: number | null) => {
    if (value === null) return null;
    const rounded = Math.round(value * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} ${strings.gramUnit}`;
  };
  const nutritionRow = (label: string, value: string | null) =>
    value === null ? null : (
      <div className="list-row" style={{ minHeight: 0 }}>
        <span className="grow t-muted">{label}</span>
        <span className="t-numeric">{value}</span>
      </div>
    );
  const hasIngredientDetails = [item.countryOfOrigin, item.composition, item.additives, item.allergens].some(Boolean);

  return (
    <Sheet onClose={onClose} size="large" label={strings.productInfo}>
      {(close) => (
        <div className="sheet-body">
          <div className="sheet-header" style={{ justifyContent: 'flex-end' }}>
            <GlassButton icon="close" label={strings.close} onClick={close} />
          </div>
          <div className="stack sheet-pad" style={{ gap: 22, paddingTop: 0 }}>
            <div style={{ marginInline: 'calc(var(--gutter) * -1)' }}>
              <ScreenTitle text={item.name} />
            </div>
            {item.promoUntil && <p className="t-muted" style={{ fontSize: 17 }}>{strings.promoUntil(longDay(lang, item.promoUntil))}</p>}
            {item.description && <p className="t-muted" style={{ fontSize: 17 }}>{item.description}</p>}
            {row(strings.gtin, item.ean)}
            {row(strings.countryOfOrigin, item.countryOfOrigin)}
            {row(strings.composition, item.composition)}
            {row(strings.additives, item.additives)}
            {row(strings.allergens, item.allergens)}
            {row(strings.shelfLife, item.shelfLifeDays ? strings.shelfLifeValue(item.shelfLifeDays) : null)}
            {row(strings.storage, item.storage)}
            {!hasIngredientDetails && <p className="t-muted">{strings.productInfoMissing}</p>}
            {!isNutritionEmpty(item.nutrition) && (
              <div className="stack" style={{ gap: 10 }}>
                <h3 className="t-display" style={{ fontSize: 18 }}>
                  {strings.nutrition}
                </h3>
                <p className="t-muted">{item.unit === 'l' ? strings.per100ml : strings.per100g}</p>
                <div className="card flush">
                  {nutritionRow(strings.energyLabel, item.nutrition.energyKcal === null ? null : `${item.nutrition.energyKcal} ${strings.kcalUnit}`)}
                  {nutritionRow(strings.proteinLabel, grams(item.nutrition.proteinG))}
                  {nutritionRow(strings.fatLabel, grams(item.nutrition.fatG))}
                  {nutritionRow(strings.carbsLabel, grams(item.nutrition.carbsG))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Sheet>
  );
}
